import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { Customer } from '../types';
import { formatPhoneForStorage } from '../utils/validation';
import { assertPhoneNotDuplicate, DUPLICATE_PHONE_MESSAGE } from '../lib/customerPhoneService';
import { useProjectsStore } from './projectsStore';

async function assertNoDuplicateContact(
  email: string | null,
  phone: string | null,
  excludeId?: number,
): Promise<void> {
  const emailNorm = email?.trim().toLowerCase() ?? '';

  if (emailNorm) {
    let q = supabase.from('customers').select('id').ilike('email', emailNorm);
    if (excludeId != null) q = q.neq('id', excludeId);
    const { data, error } = await q.limit(1).maybeSingle();
    if (error) throw error;
    if (data) throw new Error('A contact with this email already exists.');
  }

  await assertPhoneNotDuplicate(phone, excludeId);
}

interface CustomersState {
  customers: Customer[];
  loading: boolean;
  error: string | null;
  
  fetchCustomers: () => Promise<void>;
  addCustomer: (customer: Omit<Customer, 'id'>) => Promise<void>;
  updateCustomer: (id: number, customer: Partial<Customer>) => Promise<void>;
  deleteCustomer: (id: number) => Promise<void>;
}

export const useCustomersStore = create<CustomersState>((set) => ({
  customers: [],
  loading: false,
  error: null,

  fetchCustomers: async () => {
    set({ loading: true, error: null });
    try {
      const { data: customers, error } = await supabase
        .from('customers')
        .select('*')
        .order('name');

      if (error) throw error;

      set({ customers, loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },

  addCustomer: async (customer) => {
    set({ loading: true, error: null });
    try {
      const emailTrimmed = customer.email?.trim() ?? '';
      const phoneStored = formatPhoneForStorage(customer.phone);
      const payload = {
        name: customer.name,
        phone: phoneStored,
        email: emailTrimmed === '' ? null : emailTrimmed,
        location: customer.location,
        source: customer.source?.trim() || null,
        shop_code: customer.shop_code?.trim() || null,
        shop_name: customer.shop_name?.trim() || null,
        portal_application_base_price: customer.portal_application_base_price ?? null,
        ...(customer.company_id != null ? { company_id: customer.company_id } : {}),
      };

      await assertNoDuplicateContact(payload.email, payload.phone);

      const { data: newCustomer, error } = await supabase
        .from('customers')
        .insert(payload)
        .select()
        .single();

      if (error) {
        const code = String((error as { code?: string }).code ?? '');
        const msg = ((error as { message?: string }).message ?? '').toLowerCase();
        if (
          code === '23505' &&
          (msg.includes('idx_customers_email_unique') || msg.includes('(email)'))
        ) {
          throw new Error('A contact with this email already exists.');
        }
        if (code === '23505' && msg.includes('phone number already exists')) {
          throw new Error(DUPLICATE_PHONE_MESSAGE);
        }
        throw error;
      }

      set((state) => ({
        customers: [...state.customers, newCustomer as Customer],
        loading: false,
      }));
    } catch (error) {
      const message = (error as Error).message;
      set({ error: message, loading: false });
      throw error instanceof Error ? error : new Error(message);
    }
  },

  updateCustomer: async (id, customer) => {
    set({ loading: true, error: null });
    try {
      // Capture the previous name before the update so we can detect a change
      const existing = useCustomersStore.getState().customers.find((c) => c.id === id);
      const prevName = existing?.name;

      const payload: Partial<Customer> = { ...customer };
      if (customer.shop_code !== undefined) {
        payload.shop_code = customer.shop_code?.trim() || null;
      }
      if (customer.shop_name !== undefined) {
        payload.shop_name = customer.shop_name?.trim() || null;
      }
      if (customer.portal_application_base_price !== undefined) {
        payload.portal_application_base_price = customer.portal_application_base_price;
      }
      if (customer.email !== undefined) {
        const emailTrimmed = customer.email?.trim() ?? '';
        payload.email = emailTrimmed === '' ? null : emailTrimmed;
      }
      if (customer.phone !== undefined) {
        payload.phone = formatPhoneForStorage(customer.phone);
      }

      const nextEmail =
        payload.email !== undefined ? (payload.email as string | null) : (existing?.email ?? null);
      const nextPhone =
        payload.phone !== undefined ? (payload.phone as string | null) : (existing?.phone ?? null);

      await assertNoDuplicateContact(
        nextEmail?.trim() ? nextEmail.trim() : null,
        nextPhone,
        id,
      );

      const { data: updatedCustomer, error } = await supabase
        .from('customers')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        const code = String((error as { code?: string }).code ?? '');
        const msg = ((error as { message?: string }).message ?? '').toLowerCase();
        if (
          code === '23505' &&
          (msg.includes('idx_customers_email_unique') || msg.includes('(email)'))
        ) {
          throw new Error('A contact with this email already exists.');
        }
        if (code === '23505' && msg.includes('phone number already exists')) {
          throw new Error(DUPLICATE_PHONE_MESSAGE);
        }
        throw error;
      }

      const updated = updatedCustomer as Customer;

      set(state => ({
        customers: state.customers.map(c => c.id === id ? updated : c),
        loading: false,
      }));

      // If the name changed, patch the in-memory projects state so the UI
      // reflects the new name immediately (the DB trigger handles persistence).
      if (customer.name !== undefined && customer.name !== prevName) {
        useProjectsStore.setState((state) => ({
          projects: state.projects.map((p) =>
            p.customerId === id ? { ...p, customerName: updated.name } : p,
          ),
        }));
      }
    } catch (error) {
      const message = (error as Error).message;
      set({ error: message, loading: false });
      throw error instanceof Error ? error : new Error(message);
    }
  },

  deleteCustomer: async (id) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        customers: state.customers.filter(c => c.id !== id),
        loading: false
      }));
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  }
}));